import * as React from 'react';
import * as moment from 'moment';
import * as _ from 'lodash';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, ExpertSystemActionCreators, IExportSystemLookupDataResponse, } from '@nkr/store/index';
import { IExpertSystemOrderResponse, IExpertSystemRecipientResponse, } from '@nkr/store/expert-system';
import { IExpertSystemOrder, IExpertSystemRecipient, IListItem, IRecipientResponse, } from '@nkr/models/index';
import ExpertSystemAnalysis from './expert-analysis';
import ExpertAnalysisStillPending from './expert-analysis-still-pending';

interface IProps {
    centerId: number;
    orderId: number;
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>;
    hideLoading: () => void;
    getExpertSystemOrder: (orderId: number) => Promise<IExpertSystemOrderResponse>;
    updateExpertSystemOrder: (orderId: number, statusId: number) => Promise<IExpertSystemOrderResponse>;
    getExpertSystemRecipients: (centerId: number) => Promise<IExpertSystemRecipientResponse>;
    getLookupData: () => Promise<IExportSystemLookupDataResponse>;
    updateRecipient: (recipientId: number, alias: string, firstName: string, lastName: string, email: string,
        centerId: number, address1: string, address2: string, city: string, state: string,
        postalCode: string, birthDate: string, gender: string, phone: string) => Promise<IRecipientResponse>;
    placeRecipientOrder: (orderId: number, recipientId: number, shipKit: number) => Promise<IExpertSystemOrderResponse>;
    processRecipientForAnalysis: (orderId: number, recipientId: number) => Promise<IExpertSystemOrderResponse>;
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    centerId: number,
    orderId: number,
    expertOrder: IExpertSystemOrder,
    recipientInfo: IExpertSystemRecipient,
    recipientList: IExpertSystemRecipient[],
    genderList: IListItem[],
    stateList: IListItem[],
    hasAllData: boolean,
    dobOutOfBounds: boolean,
    processRecipientForAnalysis: boolean
}

class ViewExpertSystemOrder extends React.Component<TProps, IState> {
    readonly state: IState = {
        centerId: this.props.centerId,
        orderId: this.props.orderId,
        expertOrder: {
            expertSystemId: 0,
            id: 0,
            donorId: 0,
            recipientId: 0,
            donorAlias: '',
            recipientAlias: '',
            statusId: 0,
            ordersSent: '',
            donorResultsReceived: '',
            recpResultsReceived: '',
            recomendation: '',
            alertSent: '',
            alertAcknowledged: '',
            comments: '',
            recipientTyping: false,
            donorTyping: false
        },
        recipientInfo: {
            recipientId: 0,
            alias: '',
            firstName: '',
            lastName: '',
            email: '',
            centerId: 0,
            address1: '',
            address2: '',
            city: '',
            state: '',
            postalCode: '',
            birthDate: '',
            gender: '',
            phone: '',
            hasTyping: false
        },
        recipientList: [],
        genderList: [],
        stateList: [],
        hasAllData: false,
        dobOutOfBounds: false,
        processRecipientForAnalysis: false
    };

    componentDidMount = () => {
        this.getData();
    };

    getData = () => {
        let orderId = this.props.orderId;

        this.props.showLoading('Getting Data, one moment please...').then(() => {
            Promise.all([
                this.props.getExpertSystemOrder(orderId),
                this.props.getExpertSystemRecipients(this.props.centerId),
                this.props.getLookupData(),
            ]).then(([orderResponse, recipientListResponse, lookupDataResponse]) => {
                let success =
                    orderResponse.data.success
                    && recipientListResponse.data.success
                    && lookupDataResponse.data.success;

                if (success) {
                    let order = orderResponse.data.results;

                    if (order.statusId == 5) {
                        order.statusId = 6;
                        this.updateStatus(orderId, 6);
                    }

                    let { genderList, stateList } = lookupDataResponse.data.results;

                    this.setState({
                        expertOrder: order,
                        recipientList: recipientListResponse.data.results,
                        genderList,
                        stateList,
                    }, () => {
                        this.props.hideLoading();
                    });
                } else {
                    this.props.hideLoading();
                }
            });
        });

        this.checkForCompleteData();
    };

    handleReturnHome = () => {
        let dashboardUrl = '/center/' + this.props.centerId + '/action-center/expert-system/';
        let returnTo = new URLSearchParams(window.location.search).get('returnTo');

        window.location.href = returnTo != null && returnTo.startsWith(dashboardUrl) ? returnTo : dashboardUrl;
    };

    handleNoRecipient = () => {
        if (confirm('Are you sure you wish to record this donor as not having a paired recipient? (ie: a good samaritan donor?)')) {
            this.props.showLoading('Clearing expert system order one moment please...').then(() => {
                this.props.updateExpertSystemOrder(this.props.orderId, 0).then((response) => {
                    if (response.data.success) {
                        this.props.hideLoading();
                        window.location.href = '/center/' + this.props.centerId + '/action-center/expert-system/';
                    } else {
                        this.props.hideLoading();
                        alert('An error has occurred...');
                    }
                });
            });
        }
    };

    updateStatus = (orderId: number, statusId: number) => {
        if (orderId > 0) {
            this.props.showLoading('Validating Expert System Order details, one moment please...').then(() => {
                this.props.updateExpertSystemOrder(orderId, statusId).then((response) => {
                    if (response.data.success) {
                        this.props.hideLoading();
                    } else {
                        this.props.hideLoading();
                    }
                });
            });
        }
    };

    checkForCompleteData = () => {
        let recipient = this.state.recipientInfo;

        if (
            recipient.alias == null ||
            recipient.firstName == null ||
            recipient.lastName == null ||
            recipient.email == null ||
            recipient.address1 == null ||
            recipient.city == null ||
            recipient.state == null ||
            recipient.postalCode == null ||
            recipient.birthDate == null ||
            recipient.gender == null ||
            recipient.phone == null
        ) {
            this.setState({ hasAllData: false });
            return;
        }

        if (
            recipient.alias.length > 0 &&
            recipient.firstName.length > 0 &&
            recipient.lastName.length > 0 &&
            recipient.email.length > 0 &&
            recipient.address1.length > 0 &&
            recipient.city.length > 0 &&
            recipient.state.length > 0 &&
            recipient.postalCode.length > 4 &&
            recipient.birthDate.length > 0 &&
            recipient.gender.length > 0 &&
            recipient.phone.length > 9
        ) {
            this.setState({ hasAllData: true });
        } else {
            this.setState({ hasAllData: false });
        }
    };

    handleRecipientInfoChange = (recipientInfo: IExpertSystemRecipient) => {
        this.setState({
            recipientInfo,
        }, () => {
            this.checkForCompleteData();
        });
    };

    checkValidBirthdate = () => {
        let recipient = this.state.recipientInfo;
        let { birthDate } = recipient;
        let today = moment();
        let dob = moment(birthDate);

        if (!dob.isValid()) {
            return false;
        }

        if (dob.isAfter(today)) {
            return false;
        }

        return true;
    }

    handleSubmit = () => {
        let recipient = this.state.recipientInfo;

        if (!this.checkValidBirthdate()) {
            this.setState({
                dobOutOfBounds: true
            });

            return null;
        } else {
            this.setState({
                dobOutOfBounds: false
            });
        }

        this.props.showLoading('Processing, one moment please...').then(async () => {
            var recipientResponse = await this.props.updateRecipient(
                recipient.recipientId,
                recipient.alias,
                recipient.firstName,
                recipient.lastName,
                recipient.email,
                this.props.centerId,
                recipient.address1,
                recipient.address2,
                recipient.city,
                recipient.state,
                recipient.postalCode,
                recipient.birthDate,
                recipient.gender,
                recipient.phone
            );

            if (recipientResponse.data.success == false) {
                alert(recipientResponse.data.message);
                this.props.hideLoading();
            }

            recipient.recipientId = recipientResponse.data.results.id;

            let expertResponse = await this.props.placeRecipientOrder(
                this.props.orderId,
                recipientResponse.data.results.id,
                1
            );

            if (expertResponse.data.success) {
                let data = expertResponse.data.results;

                this.setState({
                    expertOrder: data,
                    recipientInfo: recipient,
                });
            }

            this.props.hideLoading();
            //Redirect to order status screen
        });
    };

    handleSubmitExistingKit = () => {
        let recipient = this.state.recipientInfo;

        if (!this.checkValidBirthdate()) {
            this.setState({
                dobOutOfBounds: true
            });

            return null;
        } else {
            this.setState({
                dobOutOfBounds: false
            });
        }

        this.props.showLoading('Processing, one moment please...').then(async () => {
            var recipientResponse = await this.props.updateRecipient(
                recipient.recipientId,
                recipient.alias,
                recipient.firstName,
                recipient.lastName,
                recipient.email,
                this.props.centerId,
                recipient.address1,
                recipient.address2,
                recipient.city,
                recipient.state,
                recipient.postalCode,
                recipient.birthDate,
                recipient.gender,
                recipient.phone
            );

            if (recipientResponse.data.success == false) {
                alert(recipientResponse.data.message);
                this.props.hideLoading();
            }

            recipient.recipientId = recipientResponse.data.results.id;

            let expertResponse = await this.props.placeRecipientOrder(
                this.props.orderId,
                recipientResponse.data.results.id,
                0
            );

            if (expertResponse.data.success) {
                let data = expertResponse.data.results;

                this.setState({
                    expertOrder: data,
                    recipientInfo: recipient,
                });
            }

            alert('You will now be redirected to the Medical Details screen to generate a requisition to ship with the kit.');
            let url = '/center/' + this.props.centerId + '/recipient/' + recipientResponse.data.results.id + '/profile/med-details?reqType=12';
            let win = window.open(url, '_blank');

            this.props.hideLoading();
            //Redirect to order status screen
        });
    };

    handleProcessRecipientForAnalysis = () => {
        let recipient = this.state.recipientInfo;

        if (!this.checkValidBirthdate()) {
            this.setState({
                dobOutOfBounds: true
            });

            return null;
        } else {
            this.setState({
                dobOutOfBounds: false
            });
        }

        this.props.showLoading('Processing, one moment please...').then(async () => {
            var recipientResponse = await this.props.updateRecipient(
                recipient.recipientId,
                recipient.alias,
                recipient.firstName,
                recipient.lastName,
                recipient.email,
                this.props.centerId,
                recipient.address1,
                recipient.address2,
                recipient.city,
                recipient.state,
                recipient.postalCode,
                recipient.birthDate,
                recipient.gender,
                recipient.phone
            );

            if (recipientResponse.data.success == false) {
                alert(recipientResponse.data.message);
                this.props.hideLoading();
            }

            recipient.recipientId = recipientResponse.data.results.id;

            let expertResponse = await this.props.processRecipientForAnalysis(
                this.props.orderId,
                recipientResponse.data.results.id
            );

            if (expertResponse.data.success) {
                let data = expertResponse.data.results;

                this.setState({
                    expertOrder: data,
                    recipientInfo: recipient,
                    processRecipientForAnalysis: true
                });
            }

            this.props.hideLoading();
            //Redirect to order status screen
        });
    };

    render = () => {
        if (this.state.expertOrder.id == 0) {
            return <div className='container'>Loading...</div>;
        }

        let donorAlias = this.state.expertOrder.donorAlias;

        if (this.state.expertOrder.statusId <= 4) {
            return (
                <ExpertAnalysisStillPending
                    order={this.state.expertOrder}
                    recipientInfo={this.state.recipientInfo}
                    recipientList={this.state.recipientList}
                    genderList={this.state.genderList}
                    stateList={this.state.stateList}
                    hasAllData={this.state.hasAllData}
                    dobOutOfBounds={this.state.dobOutOfBounds}
                    donorAlias={donorAlias}
                    processRecipientForAnalysis={this.state.processRecipientForAnalysis}
                    onDataChange={this.handleRecipientInfoChange}
                    onNoRecipient={this.handleNoRecipient}
                    onSubmit={this.handleSubmit}
                    onSubmitExistingKit={this.handleSubmitExistingKit}
                    onSubmitForAnalysis={this.handleProcessRecipientForAnalysis}
                    onReturnHome={this.handleReturnHome}
                />
            );
        } else {
            return (
                <ExpertSystemAnalysis
                    centerId={this.props.centerId}
                    order={this.state.expertOrder}
                    onReturnHome={this.handleReturnHome}
                />
            );
        }
    };
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getExpertSystemOrder: (orderId) => {
            return dispatch(ExpertSystemActionCreators.getExpertSystemOrder(orderId));
        },
        updateExpertSystemOrder: (orderId, statusId) => {
            return dispatch(ExpertSystemActionCreators.updateExpertSystemOrder(orderId, statusId));
        },
        getExpertSystemRecipients: (centerId) => {
            return dispatch(ExpertSystemActionCreators.getRecipientsForExpertSystem(centerId));
        },
        getLookupData: () => {
            return dispatch(ExpertSystemActionCreators.getLookupData());
        },
        updateRecipient: (recipientId: number, alias: string, firstName: string, lastName: string, email: string, centerId: number, address1: string, address2: string, city: string,
            state: string, postalCode: string, birthDate: string, gender: string, phone: string) => {
            return dispatch(ExpertSystemActionCreators.updateRecipient({ recipientId, alias, firstName, lastName, email, centerId, address1, address2, city, state, postalCode, birthDate, gender, phone }));
        },
        placeRecipientOrder: (orderId: number, recipientId: number, shipKit: number) => {
            return dispatch(ExpertSystemActionCreators.placeRecipientOrder(orderId, recipientId, shipKit));
        },
        processRecipientForAnalysis: (orderId: number, recipientId: number) => {
            return dispatch(ExpertSystemActionCreators.processRecipientForAnalysis(orderId, recipientId));
        },
    };
};

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(ViewExpertSystemOrder);
